import React, { useState, useEffect, useCallback } from "react";
import { Partnership } from "../Entities/Partnership";
import { Card, CardContent, CardHeader, CardTitle } from "@/Components/ui/card";
import { Badge } from "@/Components/ui/badge";
import RealTimeStatus from "@/Components/ui/real-time-status";
import { FileText, Building2, DollarSign, Calendar, TrendingUp, Users, Clock, AlertCircle, CheckCircle } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQuery, useQueryClient } from '@tanstack/react-query';

export default function Dashboard() {
  const [partnerships, setPartnerships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    no_prazo: 0,
    quitado: 0,
    em_debito: 0,
    verificar: 0,
    totalValue: 0,
    thisYear: 0,
    thisMonth: 0,
    monitoredOscs: 0,
  });

  // ✅ Socket.IO removido - atualização automática via React Query global
  // Configuração global em main.jsx + useGlobalSocket.js fazem o trabalho
  // Quando Socket.IO emite 'data_updated', React Query invalida cache automaticamente

  const loadPartnerships = useCallback(async () => {
    setIsLoading(true);
    try {
      // Usar a API de estatísticas diretamente
      const statsData = await Partnership.getStats();
      setStats(statsData);
      
      // Carregar parcerias para a lista recente (primeiras 10 mais recentes)
      const response = await Partnership.list('-created_at', {}, 1, 10);
      // Verificar se response tem a estrutura de paginação
      const data = response.data || response;
      setPartnerships(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setPartnerships([]);
    }
    setIsLoading(false);
  }, []);


  useEffect(() => {
    loadPartnerships();
  }, [loadPartnerships]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getRecentPartnerships = () => {
    if (!Array.isArray(partnerships)) {
      return [];
    }
    return partnerships
      .filter(p => p.createdAt || p.created_at)
      .sort((a, b) => {
        try {
          const dateA = typeof (a.createdAt || a.created_at) === 'string' ? parseISO(a.createdAt || a.created_at) : new Date(a.createdAt || a.created_at);
          const dateB = typeof (b.createdAt || b.created_at) === 'string' ? parseISO(b.createdAt || b.created_at) : new Date(b.createdAt || b.created_at);
          return dateB - dateA;
        } catch {
          return 0;
        }
      })
      .slice(0, 5);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, 'dd/MM/yy', { locale: ptBR }) : '';
    } catch {
      return '';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'no_prazo':
        return 'bg-blue-100 text-blue-800';
      case 'quitado':
        return 'bg-green-100 text-green-800';
      case 'em_debito':
        return 'bg-red-100 text-red-800';
      case 'verificar':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'no_prazo':
        return 'No Prazo';
      case 'quitado':
        return 'Quitado';
      case 'em_debito':
        return 'Em Débito';
      case 'verificar':
        return 'Verificar';
      default:
        return 'Indefinido';
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, description }) => (
    <Card className="relative overflow-hidden hover:shadow-lg transition-shadow">
      <div className={`absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 transform translate-x-6 -translate-y-6 ${color} rounded-full opacity-10`} />
      <CardHeader className="pb-2 sm:pb-3 p-4 sm:p-5 md:p-6">
        <div className="flex justify-between items-start gap-2 sm:gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs md:text-sm font-medium text-slate-600 truncate">{title}</p>
            <p className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 mt-1 break-words">{value}</p>
            {description && (
              <p className="text-[10px] sm:text-xs text-slate-500 mt-1 line-clamp-2">{description}</p>
            )}
          </div>
          <div className={`p-1.5 sm:p-2 md:p-3 rounded-lg sm:rounded-xl ${color} bg-opacity-20 flex-shrink-0`}>
            <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${color.replace('bg-', 'text-')}`} />
          </div>
        </div>
      </CardHeader>
    </Card>
  );

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 md:space-y-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-xs sm:text-sm md:text-base text-slate-600 mt-1">
              Visão geral das suas parcerias e contratos
            </p>
          </div>
          <div className="w-full lg:w-80 lg:flex-shrink-0">
            <RealTimeStatus />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          <StatCard
            title="Total de Parcerias"
            value={stats.total}
            icon={FileText}
            color="bg-blue-500"
            description={`${stats.thisMonth} criadas este mês`}
          />
          <StatCard
            title="OSCs Monitoradas"
            value={stats.monitoredOscs}
            icon={Building2}
            color="bg-teal-500"
            description="Organizações únicas"
          />
          <StatCard
            title="Valor Total"
            value={formatCurrency(stats.totalValue)}
            icon={DollarSign}
            color="bg-purple-500"
            description="Soma de todas as parcerias"
          />
          <StatCard
            title="Em Débito"
            value={stats.em_debito}
            icon={Clock}
            color="bg-red-500"
            description={`${stats.verificar} para verificar`}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {/* Recent Partnerships */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="p-4 sm:p-5 md:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                  Parcerias Recentes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-5 md:p-6">
                {isLoading ? (
                  <div className="space-y-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {getRecentPartnerships().map((partnership) => (
                      <div
                        key={partnership.id}
                        className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 p-3 sm:p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h4 className="font-semibold text-sm sm:text-base text-slate-900 break-words">
                              {partnership.osc}
                            </h4>
                            <Badge className={`${getStatusColor(partnership.status)} text-[10px] sm:text-xs whitespace-nowrap`}>
                              {getStatusLabel(partnership.status)}
                            </Badge>
                          </div>
                          <p className="text-xs sm:text-sm text-slate-600 truncate">
                            Processo: {partnership.processo_ano}
                          </p>
                          <p className="text-xs sm:text-sm text-slate-600 break-all">
                            CNPJ: {partnership.cnpj}
                          </p>
                        </div>
                        <div className="text-left sm:text-right flex-shrink-0">
                          <p className="font-semibold text-sm sm:text-base text-green-600">
                            {formatCurrency(partnership.valor)}
                          </p>
                          <p className="text-[10px] sm:text-xs text-slate-500">
                            {formatDate(partnership.createdAt || partnership.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {partnerships.length === 0 && (
                      <div className="text-center py-8">
                        <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        <p className="text-slate-500">Nenhuma parceria cadastrada ainda</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Status Overview */}
          <div className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="p-4 sm:p-5 md:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                  Status das Parcerias
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-5 md:p-6">
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-slate-600">No Prazo</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full flex-shrink-0"></div>
                    <span className="font-semibold text-sm sm:text-base">{stats.no_prazo}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-slate-600">Quitadas</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full flex-shrink-0"></div>
                    <span className="font-semibold text-sm sm:text-base">{stats.quitado}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-slate-600">Em Débito</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full flex-shrink-0"></div>
                    <span className="font-semibold text-sm sm:text-base">{stats.em_debito}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-slate-600">Para Verificar</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-amber-500 rounded-full flex-shrink-0"></div>
                    <span className="font-semibold text-sm sm:text-base">{stats.verificar}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 sm:p-5 md:p-6">
                <CardTitle className="text-base sm:text-lg md:text-xl">Resumo Anual</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-5 md:p-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-slate-600">Este ano</span>
                    <span className="font-semibold text-sm sm:text-base">{stats.thisYear} parcerias</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-slate-600">Este mês</span>
                    <span className="font-semibold text-sm sm:text-base">{stats.thisMonth} novas</span>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-xs sm:text-sm text-slate-600">Valor médio</span>
                      <span className="font-semibold text-xs sm:text-sm break-all">
                        {formatCurrency(stats.totalValue / (stats.total || 1))}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
